import React, { useCallback, useState } from 'react';
import { UniLink } from '../../../common/ui-kit/buttons/UniLink';
import styles from './InputTemplateOptionList.module.scss';
import { Button, ButtonColor } from '../../../common/ui-kit/buttons/Button';
import {
  InputTemplateDto,
  InputTemplateType,
  QueryFactory,
} from '@lmt/shared-types';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { CheckBox } from '../../../common/ui-kit/CheckBox';
import { isEmpty } from '../../../merge-assistant/form/components/common';
import { getPatientIdFromPath } from '../../../navigation/components/NavTab';
import PinIcon from '@lmt/unicorn-shared/app/icons/pin.svg?react';
import UnpinIcon from '@lmt/unicorn-shared/app/icons/unpin.svg?react';
import { useModal } from '../../../common/ui-kit/modal/useModal';
import SettingsIcon from '@lmt/unicorn-shared/app/icons/gear.svg?react';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { IconButton } from '../../../common/ui-kit/IconButton';

interface Props {
  fieldId: number;
  fieldName: string;
  isNumber: boolean;
  onOptionClick: (value: string) => void;
  elementRef?: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  isMultipleSelect?: boolean;
  isMultiline: boolean;
}
export const InputTemplateOptionList: React.FC<Props> = React.memo(
  function InputTemplateOptionList(props) {
    const { fieldId, onOptionClick, elementRef, onClose, fieldName, isNumber } =
      props;
    const forms = useGlobalForms();
    const i18next = useScopedTranslation('input_templates.option_list');
    const claims = useClaims();
    const [values, setValues] = useState<InputTemplateDto[]>([]);
    const modals = useModal();
    const patientId = getPatientIdFromPath(window.location.href);
    const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
      patientId!,
      {
        enabled: !!patientId,
      },
    );
    const { data: inputTemplates, refetch } =
      QueryFactory.InputTemplateQuery.useGetQuery({
        subtenantId: patientQuery.data?.subtenantId,
        type: InputTemplateType.DataBlock,
        dataBlockFieldId: fieldId,
      });

    const onChangeValue = useCallback(
      (option: InputTemplateDto, checked: boolean) => {
        if (checked) {
          setValues([...values, option]);
        } else {
          setValues(values.filter((x) => x !== option));
        }
      },
      [values],
    );

    const onSortingPriorityChanged = useCallback(
      async (option: InputTemplateDto) => {
        try {
          await QueryFactory.InputTemplateClient.patch(option.id, {
            isPinned: !option.isPinned,
          });
          await refetch();
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      },
      [],
    );

    return (
      <div
        ref={elementRef}
        className={styles.optionListContainer}
        data-test-id={'input_templates-option-list'}
      >
        <div className={styles.header}>
          <div className={styles.title}>{i18next.t('header')}</div>
          {claims.permissions.inputTemplateManagement && (
            <IconButton
              color="blue"
              testId="manage_templates"
              iconComponent={<SettingsIcon className={styles.settingsIcon} />}
              className={styles.settingsButton}
              onClick={() => {
                onClose();
                forms.openInputTemplatesEditing(
                  InputTemplateType.DataBlock,
                  fieldId,
                  isNumber,
                  props.isMultiline,
                  patientQuery.data?.subtenantId,
                  i18next.t('title', { field: fieldName }),
                );
              }}
            />
          )}
        </div>
        <div className={styles['options-container']}>
          {(inputTemplates ?? [])
            .sort((a, b) => {
              if (a.isPinned !== b.isPinned) {
                return Number(b.isPinned) - Number(a.isPinned);
              }
              return a.name.localeCompare(b.name);
            })
            .map((option) => (
              <div
                key={`${option.name}`}
                className={styles.optionContainer}
                onClick={() => onOptionClick(option.value)}
                data-test-id={'template-option-item'}
              >
                <div className={styles['option-data']}>
                  {props.isMultipleSelect &&
                    (inputTemplates ?? []).length > 1 && (
                      <div
                        className={styles['checkbox-container']}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CheckBox
                          name={`description_${option.id}`}
                          onChange={(e) =>
                            onChangeValue(option, e.currentTarget.checked)
                          }
                          className={styles.checkbox}
                        />
                      </div>
                    )}
                  <div className={styles.option}>{option.name}</div>
                </div>
                <div className={styles['pin-container']}>
                  {option.isPinned ? (
                    <PinIcon
                      className={styles.pin}
                      data-disabled={
                        !claims.permissions.inputTemplateManagement
                      }
                      onClick={async (event) => {
                        if (!claims.permissions.inputTemplateManagement) {
                          return;
                        }
                        event.stopPropagation();
                        await onSortingPriorityChanged(option);
                      }}
                    />
                  ) : (
                    <UnpinIcon
                      className={styles.pin}
                      data-disabled={
                        !claims.permissions.inputTemplateManagement
                      }
                      onClick={async (event) => {
                        if (!claims.permissions.inputTemplateManagement) {
                          return;
                        }
                        event.stopPropagation();
                        await onSortingPriorityChanged(option);
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
        </div>
        {props.isMultipleSelect &&
          !isEmpty(inputTemplates) &&
          inputTemplates!.length > 1 && (
            <div className={styles.controles}>
              <UniLink
                disabled={isEmpty(values)}
                title={i18next.t('ok_button')}
                onClick={() =>
                  onOptionClick(values.map((x) => x.value).join(', '))
                }
                color={ButtonColor.Primary}
                testId={'apply-button'}
              />
            </div>
          )}
      </div>
    );
  },
);
